import { useMemo, useState } from 'react';
import { useApp } from '../../store';
import type { Issue } from '../../types';
import { displayName, type Profiles } from '../../people';
import VendorTag from '../VendorTag';
import { epicBackground } from '../../epics';
import { rollUpWorkspaceEpics, type BoardTier, type WorkspaceEpic, type EpicHealth } from '../../epic-rollup';
import {
  LIFECYCLE_TIERS,
  LIFECYCLE_LABELS,
  type LifecycleTier,
} from '../../lifecycle';
import { MONTHS } from '../../when';
import type { PortfolioRow } from './types';
import { steadyNow } from '../../steady-now';
import { IssueTextInline } from '../IssueText';

/**
 * THE PORTFOLIO'S BY-EPIC LENS — every epic across the WHOLE workspace, grouped
 * by where it is in its life (P2 #2, screen 07). The sibling of the board-level
 * Epics screen: that one is one board, this is every board the Portfolio loaded,
 * so an epic whose cards ride on three boards is one row here, rolled up across
 * all three, sitting in its lifecycle band.
 *
 * ALL DERIVED. Owner, health, progress, due and the boards-touched count are read
 * from the member cards (src/epic-rollup.ts); the band is the tier of the board
 * holding the plurality of the epic's cards (src/lifecycle.ts). Nothing is typed.
 *
 * WHAT THIS CUT CARRIES, AND WHAT IT WAITS FOR. The cross-board roll-up and the
 * three lifecycle bands are real here and now. The RICHER band assignment — a
 * pipeline board's epics reading Pipeline, a Run/BAU board's epics reading In
 * service with an "Ongoing" cadence instead of a due date — rides on the pipeline
 * feature (#inc4) and Run/BAU (#435); until those land, a board's tier is the
 * minimal derivation (running as a project? gone fully live?), so most epics sit
 * in In delivery and the other two bands fill as boards reach them. That is
 * honest: an epic sits where its board's real state puts it, never where a guess
 * would. The cross-board detail (screen 03) and the committed overrides come with
 * their own steps.
 */

const HEALTH_WORD: Record<EpicHealth, string> = {
  ontrack: 'On track',
  atrisk: 'At risk',
  offtrack: 'Off track',
};
const HEALTH_CLS: Record<EpicHealth, string> = { ontrack: 'g', atrisk: 'a', offtrack: 'r' };

/** An ISO 'YYYY-MM-DD' to "15 Dec", read off the parts so no timezone shifts the
 *  day. MONTHS is the one month-name source (R16). */
function fmtDue(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS[(m ?? 1) - 1]}`;
}

/**
 * Whether a board is being RUN rather than built — every delivery phase done.
 * The minimal "at service" judgement the lifecycle tier needs (its richer
/** Every loaded board's lifecycle tier, read from the row's own `tier` — the
 *  explicit go-live handoff (#435 surface 2), not a "phases look done" guess. Shared
 *  by the lens and the By-epic drill so both band an epic the same way. */
export function boardTiersFromRows(rows: PortfolioRow[]): BoardTier[] {
  return rows.map((r) => ({ id: r.id, name: r.name, tier: r.tier }));
}

interface Props {
  rows: PortfolioRow[];
  cardsByBoard: Map<string, Issue[]>;
  profiles: Profiles;
  loading: boolean;
  error: string | null | undefined;
  onRetry: () => void;
  /** Open an epic's cross-board detail (screen 03), by name. */
  onOpenEpic: (name: string) => void;
}

export default function EpicsLens({ rows, cardsByBoard, profiles, loading, error, onRetry, onOpenEpic }: Props) {
  // HELD STILL WITHIN THE MINUTE, so the memos below that take `now` as a
  // dependency actually cache. `Date.now()` here was new on every render, so
  // every roll-up over every card re-ran every time. See src/steady-now.ts.
  const now = steadyNow();
  const drillTo = useApp((s) => s.drillTo);

  // The board filter — narrow the list to the epics that have a card on one board,
  // or show every epic across the workspace (the default). An epic still rolls up
  // across ALL its boards; the filter only decides which epics appear.
  const [board, setBoard] = useState<string>('all');
  // The boards offered in the filter — every board the Portfolio loaded, by name.
  const boardOptions = useMemo(
    () => [...rows].sort((a, b) => a.name.localeCompare(b.name)),
    [rows],
  );

  // Each board's lifecycle tier, derived from the state it already carries, handed
  // to the roll-up so it can band every epic. Every board the Portfolio loaded is
  // listed, project or not, so a bare kanban board's epics still appear.
  const boardTiers = useMemo<BoardTier[]>(() => boardTiersFromRows(rows), [rows]);

  const wsAll = useMemo(
    () => rollUpWorkspaceEpics(cardsByBoard, boardTiers, now),
    [cardsByBoard, boardTiers, now],
  );
  // Apply the board filter to the epic set — an epic shows when it has a card on
  // the chosen board (its roll-up still spans every board it touches).
  const ws = useMemo(() => {
    if (board === 'all') return wsAll;
    return { ...wsAll, epics: wsAll.epics.filter((e) => e.boards.some((b) => b.id === board)) };
  }, [wsAll, board]);

  const allNames = ws.epics.map((e) => e.name);
  const byTier = useMemo(() => {
    const m = new Map<LifecycleTier, WorkspaceEpic[]>();
    for (const t of LIFECYCLE_TIERS) m.set(t, []);
    for (const e of ws.epics) {
      // An epic with no tier (its boards carry none — shouldn't happen) falls to
      // the least-advanced band so it is never dropped from the screen.
      const t = e.lifecycleTier ?? 'pipeline';
      m.get(t)!.push(e);
    }
    return m;
  }, [ws.epics]);

  if (error) {
    // The same read feeds every lens, so a failed load is a failed load here too.
    return (
      <div className="pf-board" style={{ padding: '16px 14px' }}>
        <div style={{ fontSize: 13, color: 'var(--t2)', marginBottom: 12 }}>
          The portfolio could not be read. {error}
        </div>
        <button className="btn" onClick={onRetry}>Try again</button>
      </div>
    );
  }

  const epicWord = ws.epics.length === 1 ? 'epic' : 'epics';

  const renderRow = (e: WorkspaceEpic) => {
    const onBoards =
      e.boards.length === 1
        ? e.boards[0].name
        : `${e.boards.length} boards`;
    const boardsTitle = e.boards.map((b) => `${b.name} (${b.cards})`).join(', ');
    return (
      <tr key={e.name} className="epl-row epl-row-click" onClick={() => onOpenEpic(e.name)} title={`Open ${e.name}`}>
        <td>
          <div className="epics-name">
            <span className="epic-sw" style={{ background: epicBackground(e.name, allNames) }} aria-hidden="true" />
            <span className="nm">{e.name}</span>
          </div>
        </td>
        <td><div className="pf-owner">{e.owner ? displayName(e.owner, profiles) : '—'}{e.owner && <VendorTag who={e.owner} profiles={profiles} compact />}</div></td>
        <td>
          <div className="rag-cell">
            <span className={`rag ${HEALTH_CLS[e.health]}`}><span className="dot" />{HEALTH_WORD[e.health]}</span>
            {e.health !== 'ontrack' && e.healthReason ? <span className="rag-why"><IssueTextInline text={e.healthReason} /></span> : null}
          </div>
        </td>
        <td>
          <div className="pf-pct">
            <span className="fig">{e.progressPct}%</span>
            <div className="bar"><div className="fill" style={{ width: `${e.progressPct}%` }} /></div>
          </div>
        </td>
        <td className="ms">
          {e.dueAuto ? (
            <>
              <span className={e.dueState === 'overdue' ? 'epics-due-over' : undefined}>{fmtDue(e.dueAuto)}</span>
              <div className="epics-due-sub">{e.dueState === 'overdue' ? 'overdue' : 'auto'}</div>
            </>
          ) : (
            <span style={{ color: 'var(--t3)' }}>—</span>
          )}
        </td>
        {/* Cards — total, with the open count beneath (the board-level Epics
            screen's own column, brought across). */}
        <td className="pf-owner">
          {e.totalCount}
          <div className="pf-sub2">{e.openCount} open</div>
        </td>
        <td>
          <span className="epl-boards" title={boardsTitle}>{onBoards}</span>
        </td>
      </tr>
    );
  };

  return (
    <>
      <div className="pf-head">
        <div className="pf-head-l">
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--t1)' }}>Epics</div>
          <div className="pf-sub">
            {loading && ws.epics.length === 0 ? '…' : ws.epics.length} {epicWord} · grouped by lifecycle
            {board !== 'all' ? ' · one board' : ''}
          </div>
        </div>
        {/* The board filter — narrow the list to one board's epics, or show the
            whole workspace. An epic still spans every board it touches. */}
        <label className="epl-filter">
          <span className="epl-filter-k">Board</span>
          <select className="epl-filter-sel" value={board} onChange={(e) => setBoard(e.target.value)}>
            <option value="all">All boards</option>
            {boardOptions.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="pf-board">
        <table className="pf-table epl-table">
          <thead>
            <tr>
              {/* Shared columns Epic·Owner·Health·Progress·Due·Cards sit in the
                  SAME order as the board-level Epics list, so the two reads look
                  like one table; "On boards" is this lens's own cross-board column
                  and rides last. */}
              <th style={{ width: '26%' }}>Epic</th>
              <th>Owner</th>
              <th>Health</th>
              <th style={{ width: '15%' }}>Progress</th>
              <th>Due</th>
              <th>Cards</th>
              <th>On boards</th>
            </tr>
          </thead>
          {ws.epics.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={7}>
                  <div className="pf-owner" style={{ color: 'var(--t3)' }}>
                    {loading
                      ? 'Reading the workspace…'
                      : 'No epics across the workspace yet — set a card’s Epic on any board to group work under one.'}
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            LIFECYCLE_TIERS.map((tier) => {
              const band = byTier.get(tier)!;
              return (
                <tbody key={tier} className="epl-band">
                  <tr className="epl-band-head">
                    <td colSpan={7}>
                      <span className="epl-band-nm">{LIFECYCLE_LABELS[tier]}</span>
                      <span className="epl-band-n">{band.length}</span>
                    </td>
                  </tr>
                  {band.length === 0 ? (
                    <tr className="epl-band-empty">
                      <td colSpan={7}>No epics here yet.</td>
                    </tr>
                  ) : (
                    band.map(renderRow)
                  )}
                </tbody>
              );
            })
          )}
        </table>
      </div>

      <p className="hint epics-foot">
        Every epic across the workspace, grouped by where it is in its life —{' '}
        <b>{LIFECYCLE_LABELS.pipeline}</b> → <b>{LIFECYCLE_LABELS.indelivery}</b> →{' '}
        <b>{LIFECYCLE_LABELS.inservice}</b>. Owner, health, progress and the due date are read from the
        member cards; an epic sits in the band of the board holding most of its work.
        {ws.noEpic.total > 0 ? (
          <> {ws.noEpic.total} {ws.noEpic.total === 1 ? 'card' : 'cards'} carry no epic.</>
        ) : null}
        {' '}
        <button type="button" className="link epl-manage" onClick={() => drillTo('settings', 'epics')}>
          Rename or remove an epic in Settings › Workflow
        </button>
      </p>
    </>
  );
}
